/**
 * Scroll Helper Utilities
 *
 * Utility functions for scrolling containers to top
 */

const SCROLL_OPTIONS: ScrollToOptions = { top: 0, behavior: 'smooth' }

/**
 * Checks if an element is scrollable
 */
const isElementScrollable = (element: HTMLElement): boolean => {
  const style = window.getComputedStyle(element)
  const hasOverflow =
    style.overflow === 'auto' ||
    style.overflow === 'scroll' ||
    style.overflowY === 'auto' ||
    style.overflowY === 'scroll'

  const hasScrollableContent = element.scrollHeight > element.clientHeight && element.clientHeight > 0

  return hasOverflow || hasScrollableContent
}

/**
 * Finds all scrollable parent containers of a given element
 */
const findScrollableParents = (element: HTMLElement): HTMLElement[] => {
  const scrollableElements: HTMLElement[] = []
  let current: HTMLElement | null = element.parentElement

  while (current && current !== document.body && current !== document.documentElement) {
    if (isElementScrollable(current)) {
      scrollableElements.push(current)
    }
    current = current.parentElement
  }

  return scrollableElements
}

/**
 * Scrolls a single element to top safely
 */
const scrollElementToTop = (element: HTMLElement | Window | Document): void => {
  try {
    if (element === window || element instanceof Window) {
      window.scrollTo(SCROLL_OPTIONS)
    } else if (element instanceof Document || element === document.documentElement) {
      document.documentElement.scrollTo?.(SCROLL_OPTIONS)
    } else if (element === document.body) {
      document.body.scrollTo?.(SCROLL_OPTIONS)
    } else if (element instanceof HTMLElement) {
      element.scrollTo(SCROLL_OPTIONS)
    }
  } catch (error) {
    // Silently ignore scroll errors (e.g., element not scrollable)
  }
}

/**
 * Scrolls all scrollable containers to top, including window, document, body, and parent containers
 *
 * @param containerSelector - Optional selector to find the container element (defaults to FlowContainer)
 */
export const scrollAllContainersToTop = (containerSelector?: string): void => {
  // Scroll main document containers
  scrollElementToTop(window)
  scrollElementToTop(document.documentElement)
  scrollElementToTop(document.body)

  // Find and scroll parent containers
  try {
    const selector = containerSelector || '[class*="space-y-6"]'
    const container = document.querySelector(selector)

    if (container instanceof HTMLElement) {
      const scrollableParents = findScrollableParents(container)
      scrollableParents.forEach(scrollElementToTop)
    }
  } catch (error) {
    // Silently ignore errors during container search
  }
}

/**
 * Scrolls to top after React DOM updates using requestAnimationFrame
 * This ensures the scroll happens after React has finished rendering
 */
export const scrollToTopAfterRender = (containerSelector?: string): void => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      scrollAllContainersToTop(containerSelector)
    })
  })
}
